// src/components/ModalDocumentos.jsx
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { authFetch, isEmpleado } from "../services/auth";
import { listarCategoriasSocio } from "../services/sociosApi";

/* ========================= Helpers ========================= */
function normalizar(str) {
  return str?.toString().trim().toUpperCase().replace(/\s+/g, "_") || "";
}

// Quita diacríticos (vehículo/vehiculo)
function stripAcentos(s = "") {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Mapa de alias → categoría canónica usada en UI
function canonCategoria(c) {
  const raw = normalizar(c);          // p.ej. "DOCUMENTOS_VEHÍCULO"
  const sin = stripAcentos(raw);      // p.ej. "DOCUMENTOS_VEHICULO"

  // Normalización de VEHICULO con/ sin acento y variantes
  if (
    raw === "DOCUMENTOS_VEHÍCULO" ||
    sin === "DOCUMENTOS_VEHICULO" ||
    raw === "DOCUMENTOS VEHÍCULO" ||
    sin === "DOCUMENTOS VEHICULO" ||
    raw === "DOCS_VEHÍCULO" ||
    sin === "DOCS_VEHICULO" ||
    raw === "DOCUMENTOS_DEL_VEHÍCULO" ||
    sin === "DOCUMENTOS_DEL_VEHICULO"
  ) {
    return "DOCUMENTOS_VEHÍCULO";
  }

  // Singular → plural
  if (raw === "CONDUCTOR") return "CONDUCTORES";

  return raw;
}

// Slug robusto para usar en rutas
function slugifyVisible(s = "") {
  return String(s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "");
}
function conductorSlugFromInput(nombre = "", apellidos = "", dni = "") {
  const full = `${nombre || ""} ${apellidos || ""}`.trim();
  if (full) return slugifyVisible(full);
  if (dni) return slugifyVisible(dni);
  return "CONDUCTOR";
}

/** Extrae { categoriaFromPath, subs[] } desde un path tipo:
 *   /uploads/SLUG/CATEGORIA/..subcarpetas../fichero.ext
 */
function parseFromPath(path) {
  if (!path) return null;
  const clean = path.replace(/^https?:\/\/[^/]+/i, "");
  const parts = clean.split("/").filter(Boolean);
  const idx = parts.indexOf("uploads");
  if (idx === -1) return null;
  const after = parts.slice(idx + 1); // [SLUG, CATEGORÍA, ...subs, fichero]
  if (after.length < 3) return null;
  const categoriaFromPath = canonCategoria(after[1]);
  const subs = after.slice(2, -1).map(normalizar);
  return { categoriaFromPath, subs };
}

function filenameFromHeaders(res, fallback = "archivo") {
  const cd = res.headers.get("Content-Disposition") || "";
  const mStar = cd.match(/filename\*\s*=\s*([^;]+)/i);
  if (mStar) {
    const v = mStar[1].trim();
    const idx = v.indexOf("''");
    if (idx >= 0) {
      try { return decodeURIComponent(v.slice(idx + 2)); } catch {}
    }
    return v.replace(/^"(.*)"$/, "$1");
  }
  const m = cd.match(/filename\s*=\s*"?(.*?)"?\s*(;|$)/i);
  if (m) return m[1];
  return fallback;
}

function looksPreviewable(ctype) {
  if (!ctype) return false;
  const t = ctype.toLowerCase();
  return (
    t.includes("pdf") ||
    t.startsWith("image/") ||
    t.startsWith("text/") ||
    t.includes("json") ||
    t.includes("csv")
  );
}

/* ========================= Componente ========================= */
export default function ModalDocumentos({
  isOpen,
  onClose,
  socio,
  puedeEliminar = true,
  defaultCategoria = null,
  defaultSubcarpeta = "",
  onChanged = () => {},
}) {
  const puedeAdministrar = useMemo(() => isEmpleado(), []);
  const [documentos, setDocumentos] = useState({});
  const [categorias, setCategorias] = useState(() => [
    "DATOS_PERSONALES",
    "DOCUMENTOS_COOPERATIVA",
    "DOCUMENTOS_VEHÍCULO",
    "CONDUCTORES",
    "JUSTIFICANTES_DE_PAGO",
    "LIQUIDACIONES",
    "MULTAS",
    "OTROS",
  ]);
  const categoriasRef = useRef([]);
  useEffect(() => { categoriasRef.current = categorias; }, [categorias]);

  const [categoriaActiva, setCategoriaActiva] = useState("");
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDoc, setRenameDoc] = useState({ id: null, nombre: "" });
  const [renameValue, setRenameValue] = useState("");

  const [subcarpetas, setSubcarpetas] = useState([]);
  const [nuevaSubcarpeta, setNuevaSubcarpeta] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Subida
  const [staged, setStaged] = useState([]); // [{id, file, nombre, caducidad}]
  const [uploadSubcarpeta, setUploadSubcarpeta] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const socioSlug = useMemo(
    () => `${normalizar(socio?.nombre)}_${normalizar(socio?.matricula)}`,
    [socio?.nombre, socio?.matricula]
  );
  const basePrefix = useMemo(() => `/uploads/${socioSlug}/`, [socioSlug]);

  // Persistencia de estructura de CONDUCTORES por socio
  const COND_KEY = useMemo(() => `cond-struct:${socio?.id || "?"}`, [socio?.id]);
  const [condStruct, setCondStruct] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COND_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(COND_KEY, JSON.stringify(condStruct || [])); } catch {}
  }, [COND_KEY, condStruct]);

  // inputs para añadir conductor desde UI
  const [cNombre, setCNombre] = useState("");
  const [cApellidos, setCApellidos] = useState("");
  const [cDni, setCDni] = useState("");

  const addConductorStruct = useCallback(() => {
    const slug = conductorSlugFromInput(cNombre, cApellidos, cDni);
    if (!slug) return;
    // evita duplicados por slug
    if (condStruct.some((c) => c.slug === slug)) {
      setCNombre(""); setCApellidos(""); setCDni("");
      return;
    }
    setCondStruct((prev) => [
      ...prev,
      { slug, nombre: cNombre, apellidos: cApellidos, dni: cDni },
    ]);
    setCNombre(""); setCApellidos(""); setCDni("");
  }, [cNombre, cApellidos, cDni, condStruct]);

  const removeConductorStruct = useCallback((slug) => {
    setCondStruct((prev) => prev.filter((c) => c.slug !== slug));
  }, []);

  async function openDoc(docId, fallbackName = "archivo") {
    // Abre la ventana sincronamente para evitar bloqueos del navegador
    const w = window.open("", "_blank");
    let revokeUrl = null;

    try {
      if (!w) throw new Error("El navegador bloqueó la ventana emergente.");

      // Mensaje de carga
      w.document.write('<html><body style="font-family:sans-serif;padding:16px">Abriendo documento…</body></html>');

      // Descarga autenticada
      const res = await authFetch(`/api/documentos/${docId}/download`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

      const ctype = res.headers.get("Content-Type") || "";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      revokeUrl = url;

      const fname = filenameFromHeaders(res, fallbackName);

      if (looksPreviewable(ctype)) {
        w.location.replace(url);
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 60_000);
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = fname || "archivo";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 4000);
        w.close();
      }
    } catch (e) {
      if (w && !w.closed) {
        w.document.open();
        w.document.write(
          `<p style="font-family:sans-serif;color:#b00;padding:16px">Error abriendo documento: ${e?.message || e}</p>`
        );
        w.document.close();
      } else {
        alert(`Error abriendo documento: ${e?.message || e}`);
      }
      if (revokeUrl) { try { URL.revokeObjectURL(revokeUrl); } catch {} }
    }
  }

  /* -------- categorías dinámicas -------- */
  const refreshCategorias = useCallback(async () => {
    if (!socio?.id) return;

    const base = [
      "DATOS_PERSONALES",
      "DOCUMENTOS_COOPERATIVA",
      "DOCUMENTOS_VEHÍCULO",
      "CONDUCTORES",
      "JUSTIFICANTES_DE_PAGO",
      "LIQUIDACIONES",
      "MULTAS",
    ];
    const desired = defaultCategoria ? canonCategoria(defaultCategoria) : null;

    try {
      const res = await listarCategoriasSocio(socio.id);
      const srv = Array.isArray(res?.categorias)
        ? res.categorias.map(canonCategoria)
        : Array.isArray(res)
        ? res.map(canonCategoria)
        : [];
      const seen = new Set();
      const ordered = [];
      for (const c of [...base, ...srv, "OTROS"]) {
        const cc = canonCategoria(c);
        if (cc && !seen.has(cc)) { seen.add(cc); ordered.push(cc); }
      }
      setCategorias(ordered);

      setCategoriaActiva((prev) =>
        (prev && ordered.includes(prev)) ? prev :
        (desired && ordered.includes(desired)) ? desired :
        ordered[0]
      );
      setCategoriaSeleccionada((prev) =>
        (prev && ordered.includes(prev)) ? prev :
        (desired && ordered.includes(desired)) ? desired :
        ordered[0]
      );
    } catch {
      const catsPrev = categoriasRef.current && categoriasRef.current.length ? categoriasRef.current : [...base, "OTROS"];
      setCategoriaActiva((prev) => prev || (desired && catsPrev.includes(desired) ? desired : catsPrev[0]));
      setCategoriaSeleccionada((prev) => prev || (desired && catsPrev.includes(desired) ? desired : catsPrev[0]));
    }
  }, [socio?.id, defaultCategoria]);

  /* -------- fetchDocs -------- */
  const fetchDocs = useCallback(async () => {
    if (!socio?.id) return;
    try {
      let res = await authFetch(`/api/documentos/socio/${socio.id}`);
      if (!res.ok) res = await authFetch(`/api/socios/${socio.id}/documentos`);
      if (!res.ok) throw new Error("Error al cargar documentos");

      const data = await res.json();
      const agr = {};

      for (const raw of Array.isArray(data) ? data : []) {
        const archivoField = raw.archivo ?? raw.path ?? raw.ruta ?? "";
        const parsed = parseFromPath(archivoField);

        let categoria = canonCategoria(
          (parsed && parsed.categoriaFromPath) ||
          raw.categoria || raw.tipo || raw.category || "OTROS"
        );
        if (!agr[categoria]) agr[categoria] = {};

        let subs = (parsed && parsed.subs) || [];
        if (!subs.length) {
          const subField = raw.subcarpeta ?? raw.carpeta ?? raw.folder ?? raw.subfolder ?? "";
          if (subField) subs = subField.toString().split("/").filter(Boolean).map(normalizar);
        }

        let cursor = agr[categoria];
        for (const s of subs) {
          if (!cursor[s]) cursor[s] = {};
          cursor = cursor[s];
        }

        const nombreDoc =
          raw.nombre ?? raw.nombre_archivo ?? raw.filename ?? raw.file_name ??
          (archivoField ? archivoField.split("/").pop() : null) ??
          `documento_${raw.id ?? Math.random().toString(36).slice(2)}`;

        const fecha = raw.fecha_subida ?? raw.subido ?? raw.created_at ?? "";
        const caduca = raw.fecha_caducidad ?? raw.caducidad ?? raw.expira ?? "";

        const pathFinal = archivoField
          ? (/^https?:\/\//i.test(archivoField) ? archivoField : `/${archivoField.replace(/^\/+/, "")}`)
          : `${basePrefix}${categoria}/${subs.length ? subs.join("/") + "/" : ""}${nombreDoc}`;

        cursor[nombreDoc] = { type: "file", id: raw.id, fecha, caduca, path: pathFinal };
      }

      const withEmpty = { ...agr };
      const cats = categoriasRef.current || [];
      for (const cat of cats) if (!withEmpty[cat]) withEmpty[cat] = {};
      setDocumentos(withEmpty);

      const desired = defaultCategoria ? canonCategoria(defaultCategoria) : null;
      setCategoriaActiva((prev) =>
        (prev && withEmpty[prev]) ? prev :
        (desired && withEmpty[desired]) ? desired :
        (cats[0] || "OTROS")
      );
      setCategoriaSeleccionada((prev) =>
        (prev && withEmpty[prev]) ? prev :
        (desired && withEmpty[desired]) ? desired :
        (cats[0] || "OTROS")
      );
    } catch (err) {
      console.error("Error cargando documentos", err);
    }
  }, [socio?.id, defaultCategoria, basePrefix]);

  /* -------- ★ Listar subcarpetas RECURSIVO (categoría + subpath) -------- */
  const fetchSubcarpetas = useCallback(async () => {
    if (!categoriaSeleccionada || !socio?.id) return;

    // Recolector BFS hasta 4 niveles por defecto
    const MAX_DEPTH = 4;
    const queue = [{ subpath: null, depth: 0 }];
    const collected = new Set();

    try {
      while (queue.length) {
        const { subpath, depth } = queue.shift();
        const qs = new URLSearchParams({ categoria: categoriaSeleccionada });
        if (subpath) qs.set("subpath", subpath);
        const url = `/api/documentos/${socio.id}/carpetas?${qs.toString()}`;

        const res = await authFetch(url);
        if (!res.ok) throw new Error(`Error al cargar subcarpetas (${res.status})`);
        const items = await res.json();

        for (const name of Array.isArray(items) ? items : []) {
          const current = subpath ? `${subpath}/${name}` : name;
          if (!collected.has(current)) {
            collected.add(current);
            if (depth + 1 < MAX_DEPTH) {
              queue.push({ subpath: current, depth: depth + 1 });
            }
          }
        }
      }

      // Integrar estructura de conductores “virtual” si procede
      const extra = [];
      if (canonCategoria(categoriaSeleccionada) === "CONDUCTORES" && Array.isArray(condStruct)) {
        for (const c of condStruct) {
          extra.push(`${c.slug}`);
          extra.push(`${c.slug}/DATOS_PERSONALES`);
          extra.push(`${c.slug}/NOMINAS`);
        }
      }

      const merged = Array.from(new Set([...collected, ...extra]));
      // Orden: por número de segmentos (padres antes que hijos) y alfabético
      merged.sort((a, b) => {
        const da = a.split("/").length - b.split("/").length;
        if (da !== 0) return da;
        return a.localeCompare(b, "es");
      });

      setSubcarpetas(merged);
    } catch (err) {
      console.error("Error cargando subcarpetas:", err);
      setSubcarpetas([]);
    }
  }, [categoriaSeleccionada, socio?.id, condStruct]);

  /* -------- Efectos -------- */
  useEffect(() => {
    if (!isOpen || !socio?.id) return;
    let active = true;
    (async () => {
      try {
        await refreshCategorias();
        if (!active) return;
        await fetchDocs();
        setStaged([]);
      } catch {}
    })();
    return () => { active = false; };
  }, [isOpen, socio?.id, refreshCategorias, fetchDocs]);

  // Preseleccionar subcarpeta por defecto si existe
  useEffect(() => {
    if (!isOpen) return;
    if (!defaultSubcarpeta) { setUploadSubcarpeta(""); return; }
    const found = subcarpetas.find((s) => s.toUpperCase() === defaultSubcarpeta.toUpperCase());
    setUploadSubcarpeta(found ? found : "");
  }, [isOpen, subcarpetas, defaultSubcarpeta]);

  useEffect(() => {
    if (!categoriaSeleccionada || !socio?.id) return;
    setSubcarpetas([]);
    setUploadSubcarpeta("");
    fetchSubcarpetas(); // ★ ahora recursivo
  }, [categoriaSeleccionada, socio?.id, fetchSubcarpetas]);

  /* -------- Estados / badges -------- */
  const getEstadoCaducidad = (caduca) => {
    if (!caduca) return { estado: "nocad", dias: null };
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const d = new Date(`${caduca}T00:00:00`);
    const diffDias = Math.ceil((d - hoy) / 86400000);
    if (diffDias < 0) return { estado: "expired", dias: diffDias };
    if (diffDias <= 30) return { estado: "soon", dias: diffDias };
    return { estado: "ok", dias: diffDias };
  };

  /* -------- Acciones doc -------- */
  const handleDelete = async (idDoc, nombreArchivo) => {
    if (!window.confirm(`¿Seguro que quieres eliminar "${nombreArchivo}"?`)) return;
    try {
      const res = await authFetch(`/api/documentos/${idDoc}`, { method: "DELETE", headers: { Accept: "application/json" } });
      const contentType = res.headers.get("content-type");
      const data = contentType && contentType.includes("application/json") ? await res.json() : { message: await res.text() };
      if (!res.ok) throw new Error(data.detail || data.message || "Error");
      alert(data.message || "Documento eliminado correctamente");
      await fetchDocs();
      onChanged?.();
    } catch (error) {
      console.error("Error al eliminar el documento:", error);
      alert(`Error al eliminar el documento: ${error.message}`);
    }
  };

  const openRename = (idDoc, nombreActual) => {
    setRenameDoc({ id: idDoc, nombre: nombreActual });
    setRenameValue(nombreActual);
    setRenameOpen(true);
  };

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!renameDoc.id || !renameValue?.trim()) return;
    try {
      const formData = new FormData();
      formData.append("nuevo_nombre", renameValue.trim());
      const res = await authFetch(`/api/documentos/${renameDoc.id}/rename`, { method: "PATCH", body: formData });
      const contentType = res.headers.get("content-type");
      const data = contentType && contentType.includes("application/json") ? await res.json() : { message: await res.text() };
      if (!res.ok) throw new Error(data.detail || data.message || "No se pudo renombrar");
      setRenameOpen(false);
      alert("Documento renombrado correctamente");
      await fetchDocs();
      onChanged?.();
    } catch (err) {
      console.error("Error al renombrar:", err);
      alert(`Error al renombrar: ${err.message}`);
    }
  };

  // Eliminar subcarpeta (manejo robusto body + query + recursivo)
  const eliminarSubcarpeta = useCallback(
    async (nombre) => {
      if (!window.confirm(`¿Seguro que quieres eliminar la carpeta "${nombre}"? Se eliminará también todo su contenido.`)) {
        return;
      }

      // Helpers DELETE
      const delBody = async (recursive = false) =>
        authFetch(`/api/documentos/${socio.id}/carpeta`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            categoria: categoriaSeleccionada || categoriaActiva,
            nombre_subcarpeta: nombre,
            recursive,
          }),
        });

      const delQS = async (recursive = false) => {
        const qs = new URLSearchParams({
          categoria: categoriaSeleccionada || categoriaActiva,
          nombre_subcarpeta: nombre,
          recursive: recursive ? "1" : "0",
        });
        return authFetch(`/api/documentos/${socio.id}/carpeta?${qs.toString()}`, { method: "DELETE" });
      };

      try {
        // 1) DELETE con body (no recursivo)
        let r = await delBody(false);
        if (!r.ok) {
          const txt = await r.text().catch(() => "");
          if (/Directorio no vacío/i.test(txt)) {
            // 2) DELETE con body (recursivo)
            r = await delBody(true);
            if (!r.ok) {
              const txt2 = await r.text().catch(() => "");
              // 3) Fallback querystring no recursivo
              r = await delQS(false);
              if (!r.ok) {
                const txt3 = await r.text().catch(() => "");
                if (/Directorio no vacío/i.test(txt3)) {
                  // 4) Fallback querystring recursivo
                  r = await delQS(true);
                  if (!r.ok) throw new Error(txt3 || "No se pudo eliminar");
                } else {
                  throw new Error(txt2 || txt || "No se pudo eliminar");
                }
              }
            }
          } else {
            // fallback query directamente
            r = await delQS(false);
            if (!r.ok) {
              const t = await r.text().catch(() => "");
              throw new Error(t || "No se pudo eliminar");
            }
          }
        }

        alert("Carpeta eliminada correctamente");
        await fetchDocs();
        await fetchSubcarpetas();
        onChanged?.();
      } catch (err) {
        alert(`Error al eliminar carpeta: ${err?.message || err}`);
      }
    },
    [socio?.id, categoriaSeleccionada, categoriaActiva, fetchDocs, fetchSubcarpetas, onChanged]
  );

  /* -------- Subida: helper ROBUSTO -------- */
  const pushFiles = useCallback((src) => {
    let files = [];
    if (src instanceof FileList) {
      files = Array.from(src);
    } else if (Array.isArray(src)) {
      files = src;
    } else if (src?.files && src.files.length) {
      files = Array.from(src.files);
    } else if (src?.items && src.items.length) {
      files = Array.from(src.items)
        .filter((it) => it.kind === "file")
        .map((it) => it.getAsFile())
        .filter(Boolean);
    } else if (src instanceof File) {
      files = [src];
    }
    if (!files.length) return;
    setStaged((prev) => ([
      ...prev,
      ...files.map((file) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        file,
        nombre: file.name.replace(/\.[^/.]+$/, "") || file.name,
        caducidad: "",
      })),
    ]));
  }, []);

  /* -------- Subcarpetas derivadas para CONDUCTORES -------- */
  const derivedSubcarpetas = useMemo(() => {
    const base = Array.isArray(subcarpetas) ? subcarpetas.slice() : [];
    if (canonCategoria(categoriaSeleccionada) === "CONDUCTORES" && Array.isArray(condStruct)) {
      for (const c of condStruct) {
        const a = `${c.slug}/DATOS_PERSONALES`;
        const b = `${c.slug}/DATOS_COOPERATIVA`;
        const cN = `${c.slug}/NOMINAS`;
        if (!base.includes(a)) base.push(a);
        if (!base.includes(b)) base.push(b);
        if (!base.includes(cN)) base.push(cN);
      }
    }
    // Ordenar: rutas padres primero y alfabético
    return base
      .slice()
      .sort((x, y) => {
        const dx = x.split("/").length - y.split("/").length;
        if (dx !== 0) return dx;
        return x.localeCompare(y, "es");
      });
  }, [subcarpetas, condStruct, categoriaSeleccionada]);

  /* -------- Render árbol -------- */
  const renderContenido = (contenido = {}, nivel = 0) =>
    Object.entries(contenido).map(([nombre, info]) => {
      if (info.type === "file") {
        const { estado, dias } = getEstadoCaducidad(info.caduca);
        const borderClass =
          estado === "expired" ? "border-red-300" :
          estado === "soon"   ? "border-amber-300" :
          "border-gray-200";
        const badgeClass =
          estado === "expired" ? "bg-red-100 text-red-800" :
          estado === "soon"   ? "bg-amber-100 text-amber-800" :
          estado === "nocad"  ? "bg-gray-100 text-gray-700" :
          "bg-green-100 text-green-800";
        const badgeText =
          estado === "expired" ? "Caducado" :
          estado === "soon"   ? `Caduca en ${dias} d` :
          estado === "nocad"  ? "Sin caducidad" :
          "Vigente";

        return (
          <div
            key={nombre}
            className={`flex items-center justify-between px-4 py-2 bg-white border ${borderClass} rounded shadow hover:bg-gray-50`}
            style={{ marginLeft: `${nivel * 16}px` }}
          >
            <button onClick={() => openDoc(info.id, nombre)} className="flex items-center gap-2 text-left truncate" title="Abrir documento">
              📄 <span className="truncate">{nombre}</span>
              <span className="text-xs text-gray-500 ml-2">
                (subida: {info.fecha}{info.caduca ? ` · caduca: ${info.caduca}` : ""})
              </span>
              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${badgeClass}`}>{badgeText}</span>
            </button>

            <div className="flex items-center gap-2">
              <button onClick={() => openRename(info.id, nombre)} className="text-blue-600 hover:text-blue-800 text-lg" title="Renombrar documento" aria-label="Renombrar documento">✏️</button>
              {puedeEliminar && puedeAdministrar && (
                <button onClick={() => handleDelete(info.id, nombre)} className="text-red-500 hover:text-red-700 text-lg" title="Eliminar documento" aria-label="Eliminar documento">🗑️</button>
              )}
            </div>
          </div>
        );
      }
      // Carpeta
      return (
        <div key={nombre} style={{ marginLeft: `${nivel * 16}px` }}>
          <h4 className="font-semibold text-sm mb-1">📁 {nombre}</h4>
          <div className="ml-4 space-y-1">{renderContenido(info, nivel + 1)}</div>
        </div>
      );
    });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
      <div className="bg-white w-[90%] max-w-6xl rounded-lg shadow-lg p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Documentos de {socio?.nombre || "Socio"}</h2>
          <button
            onClick={() => { setStaged([]); onClose?.(); }}
            className="text-gray-600 hover:text-red-500 text-2xl"
            aria-label="Cerrar"
          >
            &times;
          </button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar categorías dinámicas */}
          <aside className="w-1/4 border-r pr-4">
            <ul className="space-y-2">
              {categorias.map((cat) => (
                <li key={cat}>
                  <button
                    onClick={() => {
                      const nc = canonCategoria(cat);
                      setCategoriaActiva(nc);
                      setCategoriaSeleccionada(nc);
                      setNuevaSubcarpeta("");
                      setStaged([]);
                      setUploadSubcarpeta("");
                    }}
                    className={`w-full text-left px-3 py-2 rounded font-medium ${
                      categoriaActiva === canonCategoria(cat) ? "bg-blue-100 text-blue-800" : "hover:bg-gray-100"
                    }`}
                  >
                    📁 {cat.replace(/_/g, " ")}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Contenido principal */}
          <section className="w-3/4 space-y-4">
            <h3 className="text-lg font-semibold mb-2 border-b pb-1">
              {(categoriaActiva || "").replace(/_/g, " ")}
            </h3>

            {/* Leyenda */}
            <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
              <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Vigente</span>
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Caduca ≤ 30 días</span>
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">Caducado</span>
              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">Sin caducidad</span>
            </div>

            {/* Árbol de documentos */}
            {categoriaActiva && renderContenido(documentos[categoriaActiva] || {})}

            {/* ★ Subcarpetas (vacías o sin ficheros indexados), incluyendo anidadas */}
            {derivedSubcarpetas
              .filter((full) => {
                // Si esa ruta ya está materializada con ficheros en el árbol, no la repetimos
                const parts = full.split("/").map(normalizar);
                let cursor = documentos[categoriaActiva] || {};
                for (const p of parts) {
                  if (!cursor[p]) return true; // aún no existe como rama con archivos
                  cursor = cursor[p];
                }
                // Existe la ruta en el árbol, pero podría estar vacía (sin files), la mostramos como “vacía”
                return true;
              })
              .map((full) => {
                const depth = full.split("/").length - 1;
                return (
                  <div key={full} className="flex justify-between items-center border p-2 rounded bg-gray-50"
                    style={{ marginLeft: `${Math.max(0, depth) * 16}px` }}>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">📁 {full}</h4>
                      <p className="text-xs text-gray-500 italic">Carpeta vacía o sin ficheros visibles</p>
                    </div>
                    {puedeEliminar && puedeAdministrar && (
                      <button className="text-red-600 hover:text-red-800 text-sm" onClick={() => eliminarSubcarpeta(full)}>
                        Eliminar
                      </button>
                    )}
                  </div>
                );
              })}

            {/* Crear subcarpeta */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const nombre = nuevaSubcarpeta.trim();
                if (!nombre) return;

                setCreatingFolder(true);
                try {
                  const res = await authFetch(`/api/documentos/${socio.id}/carpeta`, {
                    method: "POST",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify({ categoria: categoriaSeleccionada, nombre_subcarpeta: nombre }),
                  });
                  if (!res.ok) {
                    const txt = await res.text().catch(() => "");
                    alert(`No se pudo crear la carpeta (${res.status}). ${txt}`);
                    return;
                  }
                  setNuevaSubcarpeta("");
                  await fetchSubcarpetas(); // ★ refresco recursivo
                  await fetchDocs();
                  onChanged?.();
                } catch (err) {
                  alert(`Error de red creando la carpeta: ${err?.message || err}`);
                } finally {
                  setCreatingFolder(false);
                }
              }}
              className="border-t pt-4 mt-4 space-y-3"
            >
              <label className="text-sm font-medium">Crear subcarpeta vacía</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nuevaSubcarpeta}
                  onChange={(e) => setNuevaSubcarpeta(e.target.value)}
                  placeholder="p. ej. Juan_Perez/DATOS_PERSONALES"
                  className="border p-2 rounded w-full"
                />
                <button
                  type="submit"
                  disabled={creatingFolder}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingFolder ? "Creando…" : "Crear"}
                </button>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Puedes anidar carpetas usando “/”. Los nombres se normalizan.</p>
            </form>

            {/* UI de Conductores (solo visible en categoría CONDUCTORES) */}
            {canonCategoria(categoriaSeleccionada) === "CONDUCTORES" && (
              <div className="mt-4 p-3 rounded border bg-gray-50">
                <div className="text-sm font-medium mb-2">Estructura de conductores</div>
                <div className="grid md:grid-cols-3 gap-2">
                  <input
                    className="border rounded px-2 py-1"
                    placeholder="Nombre"
                    value={cNombre}
                    onChange={(e) => setCNombre(e.target.value)}
                  />
                  <input
                    className="border rounded px-2 py-1"
                    placeholder="Apellidos"
                    value={cApellidos}
                    onChange={(e) => setCApellidos(e.target.value)}
                  />
                  <input
                    className="border rounded px-2 py-1"
                    placeholder="DNI/NIE (opcional)"
                    value={cDni}
                    onChange={(e) => setCDni(e.target.value)}
                  />
                </div>
                <div className="mt-2">
                  <button
                    className="px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={addConductorStruct}
                  >
                    + Añadir conductor
                  </button>
                </div>

                {condStruct.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {condStruct.map((c) => (
                      <div key={c.slug} className="flex items-center justify-between text-sm bg-white rounded border px-3 py-2">
                        <div>
                          <div className="font-mono">CONDUCTORES/{c.slug}/</div>
                          <div className="text-xs text-gray-600">…/DATOS_PERSONALES, …/NOMINAS</div>
                        </div>
                        <button className="text-red-600 hover:underline" onClick={() => removeConductorStruct(c.slug)}>
                          eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-600">
                  Estas subcarpetas se muestran aquí de inmediato; el servidor las materializará al subir el primer archivo dentro.
                </p>
              </div>
            )}

            {/* SUBIDA: drag & drop + bandeja */}
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                const dt = e.dataTransfer;
                if (!dt) return;
                if (dt.files && dt.files.length) {
                  pushFiles(dt.files);
                } else if (dt.items && dt.items.length) {
                  pushFiles(dt); // DataTransfer con .items
                }
              }}
              className="border-t pt-4 mt-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="text-sm font-medium">Subcarpeta (opcional)</label>
                  <select
                    className="border p-2 rounded w-full"
                    value={uploadSubcarpeta}
                    onChange={(e) => setUploadSubcarpeta(e.target.value)}
                  >
                    <option value="">(ninguna)</option>
                    {derivedSubcarpetas.map((carpeta) => (
                      <option key={carpeta} value={carpeta}>{carpeta}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium block">Arrastra archivos aquí o selecciónalos</label>
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      className="border px-3 py-2 rounded hover:bg-gray-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Elegir archivos…
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="*/*"
                      className="hidden"
                      onChange={(e) => {
                        const { files } = e.target;
                        if (files && files.length) {
                          pushFiles(files);
                          e.target.value = ""; // permite volver a elegir lo mismo
                        }
                      }}
                    />
                    <span className="text-xs text-gray-500">Puedes soltar varios a la vez.</span>
                  </div>
                </div>
              </div>

              {/* Bandeja SIEMPRE visible */}
              <div className="mt-4 border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">
                    Archivos en cola ({staged.length})
                  </div>
                  <button
                    onClick={() => setStaged([])}
                    className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                    disabled={uploading || staged.length === 0}
                    title={staged.length ? "Vaciar cola" : "No hay archivos en cola"}
                  >
                    Vaciar
                  </button>
                </div>

                {staged.length === 0 ? (
                  <div className="text-xs text-gray-500 italic">
                    No hay archivos en la cola.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {staged.map((it) => (
                      <div key={it.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                        <div className="md:col-span-6 truncate">
                          <input
                            type="text"
                            className="w-full border rounded px-2 py-1"
                            value={it.nombre}
                            onChange={(e) =>
                              setStaged((prev) => prev.map((x) => (x.id === it.id ? { ...x, nombre: e.target.value } : x)))
                            }
                            title="Nombre lógico (sin extensión, opcional)"
                          />
                          <div className="text-xs text-gray-500">
                            {it.file.name} · {(it.file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <div className="md:col-span-4">
                          <input
                            type="date"
                            className="w-full border rounded px-2 py-1"
                            value={it.caducidad}
                            onChange={(e) =>
                              setStaged((prev) => prev.map((x) => (x.id === it.id ? { ...x, caducidad: e.target.value } : x)))
                            }
                            min={new Date().toISOString().split("T")[0]}
                            title="Fecha de caducidad (opcional)"
                          />
                        </div>
                        <div className="md:col-span-2 flex justify-end">
                          <button
                            onClick={() => setStaged((prev) => prev.filter((x) => x.id !== it.id))}
                            className="text-red-600 hover:text-red-800 text-sm"
                            disabled={uploading}
                          >
                            Quitar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 flex justify-end">
                  <button
                    onClick={async () => {
                      if (!staged.length) return;
                      setUploading(true);
                      const categoria = categoriaSeleccionada || categoriaActiva;
                      const socioNombre = normalizar(socio?.nombre || "NOMBRE");
                      const socioMatricula = normalizar(socio?.matricula || "0000XXX");

                      let ok = 0, fail = 0;
                      for (const item of staged) {
                        try {
                          const formData = new FormData();
                          formData.append("archivo", item.file);
                          formData.append("nombre", item.nombre);
                          formData.append("tipo", categoria);
                          formData.append("categoria", categoria);
                          formData.append("subcarpeta", uploadSubcarpeta || "");
                          if (item.caducidad) formData.append("fecha_caducidad", item.caducidad);
                          formData.append("propietario", socioNombre);
                          formData.append("socio_nombre", socioNombre);
                          formData.append("socio_matricula", socioMatricula);
                          formData.append("socio_id", socio.id);

                          const res = await authFetch("/api/documentos/", { method: "POST", body: formData });
                          if (!res.ok) fail++; else ok++;
                        } catch { fail++; }
                      }

                      if (ok) alert(`Subida completada: ${ok} archivo(s) subido(s).`);
                      if (fail) alert(`Fallaron ${fail} archivo(s).`);
                      setUploading(false);
                      setStaged([]);
                      await fetchDocs();
                      await fetchSubcarpetas(); // ★ refresco recursivo
                      onChanged?.();
                    }}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={uploading || staged.length === 0}
                    title={staged.length ? "Subir todos los archivos de la cola" : "Añade archivos para activar la subida"}
                  >
                    {uploading ? "Subiendo…" : `Subir ${staged.length} archivo(s)`}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Modal renombrar */}
      {renameOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white w-[90%] max-w-md rounded-lg p-5 shadow-lg">
            <h4 className="text-lg font-semibold mb-3">Renombrar documento</h4>
            <form onSubmit={submitRename} className="space-y-3">
              <div>
                <label className="text-sm text-gray-700">Nuevo nombre (sin extensión)</label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="mt-1 border p-2 rounded w-full"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setRenameOpen(false)} className="px-3 py-2 rounded border hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
